import React from "react";
import {
  Activity,
  BadgeCheck,
  Boxes,
  Calendar,
  Cloud,
  Cpu,
  Eye,
  FileImage,
  FileText,
  Layers,
  Loader2,
  MessageSquare,
  Network,
  Pencil,
  Plus,
  Server,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Tag,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import type { SystemCharacteristicsResponse } from "../../../../api/frameworksApi";
import {
  FormDatePicker,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
} from "../../../components/Modal";
import { SYSTEM_STATUSES, toddmmyyyy } from "../constants";
import { LoadingSpinner } from "../components/LoadingSpinner";
import {
  SysCharRow,
  SysLevelPill,
  SysStatusPill,
} from "../components/SysDisplay";

import type { SysCharsFormShape, DiagramFile } from "../types";
export type { SysCharsFormShape, DiagramFile };

export interface SystemCharacteristicsTabProps {
  sysCharsLoading: boolean;
  sysChars: SystemCharacteristicsResponse | null;
  setSysChars: React.Dispatch<
    React.SetStateAction<SystemCharacteristicsResponse | null>
  >;
  editingSysChars: boolean;
  setEditingSysChars: React.Dispatch<React.SetStateAction<boolean>>;
  sysCharsForm: SysCharsFormShape;
  setSysCharsForm: React.Dispatch<React.SetStateAction<SysCharsFormShape>>;
  savingSysChars: boolean;
  handleSaveSysChars: () => void;
  isPublishedSsp: boolean;
  networkArchDiagram: DiagramFile | null;
  dataFlowDiagram: DiagramFile | null;
  uploadingDiagram: string | null;
  handleDiagramUpload: (
    file: File,
    type: "network" | "dataflow",
  ) => Promise<void> | void;
  handleDiagramDelete: (type: "network" | "dataflow") => Promise<void> | void;
  setViewingDiagram: React.Dispatch<
    React.SetStateAction<{
      title: string;
      contentEvidenceId: string;
      fileName?: string;
    } | null>
  >;
}

export function SystemCharacteristicsTab({
  sysCharsLoading,
  sysChars,
  setSysChars,
  editingSysChars,
  setEditingSysChars,
  sysCharsForm,
  setSysCharsForm,
  savingSysChars,
  handleSaveSysChars,
  isPublishedSsp,
  networkArchDiagram,
  dataFlowDiagram,
  uploadingDiagram,
  handleDiagramUpload,
  handleDiagramDelete,
  setViewingDiagram,
}: SystemCharacteristicsTabProps) {
  return (
    <>
      {sysCharsLoading ? (
        <LoadingSpinner text="Loading system characteristics" />
      ) : sysChars ? (
        <div className="rounded-lg border border-border bg-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center size-8 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                <Shield className="size-4" />
              </div>
              <h3 className="text-sm font-bold text-foreground">
                System Characteristics
              </h3>
            </div>
            {!editingSysChars && !isPublishedSsp && (
              <button
                onClick={() => {
                  setSysCharsForm({
                    systemName: sysChars.systemName || "",
                    systemNameShort: sysChars.systemNameShort || "",
                    description: sysChars.description || "",
                    securitySensitivityLevel:
                      sysChars.securitySensitivityLevel || "",
                    systemType: sysChars.systemType || "",
                    authorizationType: sysChars.authorizationType || "",
                    deploymentModel: sysChars.deploymentModel || "",
                    serviceModel: sysChars.serviceModel || "",
                    systemIds: sysChars.systemIds?.length
                      ? sysChars.systemIds
                      : [],
                    authorizationBoundary:
                      sysChars.authorizationBoundary?.description || "",
                    networkArchitecture:
                      sysChars.networkArchitecture?.description || "",
                    dataFlow: sysChars.dataFlow?.description || "",
                    confidentiality:
                      sysChars.securityImpactLevel?.[
                      "security-objective-confidentiality"
                      ] || "",
                    integrity:
                      sysChars.securityImpactLevel?.[
                      "security-objective-integrity"
                      ] || "",
                    availability:
                      sysChars.securityImpactLevel?.[
                      "security-objective-availability"
                      ] || "",
                    status: sysChars.status || "",
                    statusRemarks: sysChars.statusRemarks || "",
                    dateAuthorized: sysChars.dateAuthorized
                      ? sysChars.dateAuthorized.split("T")[0]
                      : "",
                  });
                  setEditingSysChars(true);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
              >
                <Pencil className="size-3" /> Edit
              </button>
            )}
          </div>

          {editingSysChars ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="System Name" required>
                  <FormInput
                    value={sysCharsForm.systemName}
                    onChange={(v) =>
                      setSysCharsForm((f) => ({ ...f, systemName: v }))
                    }
                    placeholder="Full system name"
                  />
                </FormField>
                <FormField label="Short Name">
                  <FormInput
                    value={sysCharsForm.systemNameShort}
                    onChange={(v) =>
                      setSysCharsForm((f) => ({ ...f, systemNameShort: v }))
                    }
                    placeholder="Acronym"
                  />
                </FormField>
              </div>
              <FormField label="Description">
                <FormTextarea
                  value={sysCharsForm.description}
                  onChange={(v) =>
                    setSysCharsForm((f) => ({ ...f, description: v }))
                  }
                  placeholder="System description"
                  rows={2}
                />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="System Type">
                  <FormSelect
                    options={[
                      {
                        value: "general-support-system",
                        label: "General Support System",
                      },
                      {
                        value: "major-application",
                        label: "Major Application",
                      },
                      {
                        value: "minor-application",
                        label: "Minor Application",
                      },
                      {
                        value: "cloud-service-provider",
                        label: "Cloud Service Provider",
                      },
                    ]}
                    value={sysCharsForm.systemType}
                    onChange={(v) =>
                      setSysCharsForm((f) => ({ ...f, systemType: v }))
                    }
                    placeholder="Select type"
                  />
                </FormField>
                <FormField label="Authorization Type">
                  <FormSelect
                    options={[
                      { value: "FISMA", label: "FISMA" },
                      { value: "FedRAMP", label: "FedRAMP" },
                      { value: "DoD RMF", label: "DoD RMF" },
                      { value: "StateRAMP", label: "StateRAMP" },
                      { value: "Other", label: "Other" },
                    ]}
                    value={sysCharsForm.authorizationType}
                    onChange={(v) =>
                      setSysCharsForm((f) => ({
                        ...f,
                        authorizationType: v,
                      }))
                    }
                    placeholder="Select auth type"
                  />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Deployment Model">
                  <FormSelect
                    options={[
                      { value: "public-cloud", label: "Public Cloud" },
                      { value: "private-cloud", label: "Private Cloud" },
                      {
                        value: "government-cloud",
                        label: "Government Cloud",
                      },
                      { value: "hybrid-cloud", label: "Hybrid Cloud" },
                      { value: "on-premise", label: "On-Premise" },
                    ]}
                    value={sysCharsForm.deploymentModel}
                    onChange={(v) =>
                      setSysCharsForm((f) => ({ ...f, deploymentModel: v }))
                    }
                    placeholder="Select deployment model"
                  />
                </FormField>
                <FormField label="Service Model">
                  <FormSelect
                    options={[
                      { value: "saas", label: "SaaS" },
                      { value: "paas", label: "PaaS" },
                      { value: "iaas", label: "IaaS" },
                    ]}
                    value={sysCharsForm.serviceModel}
                    onChange={(v) =>
                      setSysCharsForm((f) => ({ ...f, serviceModel: v }))
                    }
                    placeholder="Select service model"
                  />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Security Sensitivity Level">
                  <FormSelect
                    options={[
                      { value: "low", label: "Low" },
                      { value: "moderate", label: "Moderate" },
                      { value: "high", label: "High" },
                    ]}
                    value={sysCharsForm.securitySensitivityLevel}
                    onChange={(v) =>
                      setSysCharsForm((f) => ({
                        ...f,
                        securitySensitivityLevel: v,
                      }))
                    }
                    placeholder="Select level"
                  />
                </FormField>
                <FormField label="System Status">
                  <FormSelect
                    options={SYSTEM_STATUSES}
                    value={sysCharsForm.status}
                    onChange={(v) =>
                      setSysCharsForm((f) => ({ ...f, status: v }))
                    }
                    placeholder="Select status"
                  />
                </FormField>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-4 items-start">
                  {/* Left */}
                  <FormField label="Status Remarks">
                    <FormInput
                      value={sysCharsForm.statusRemarks}
                      onChange={(v) =>
                        setSysCharsForm((f) => ({ ...f, statusRemarks: v }))
                      }
                      placeholder="Status remarks"
                    />
                  </FormField>

                  {/* Right */}
                  <div className="pt-7">
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium text-foreground">
                        System IDs
                      </label>
                      <button
                        onClick={() =>
                          setSysCharsForm((f) => ({
                            ...f,
                            systemIds: [
                              ...f.systemIds,
                              {
                                "identifier-type": "https://ietf.org/rfc/rfc4122",
                                id: "",
                              },
                            ],
                          }))
                        }
                        className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                      >
                        {"+ Add ID"}
                      </button>
                    </div>

                    {/* System ID rows */}
                  </div>
                </div>
                {sysCharsForm.systemIds.map((sid, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end"
                  >
                    <FormField label={idx === 0 ? "Identifier Type" : ""}>
                      <FormInput
                        value={sid["identifier-type"]}
                        onChange={(v) =>
                          setSysCharsForm((f) => {
                            const updated = [...f.systemIds];
                            updated[idx] = {
                              ...updated[idx],
                              "identifier-type": v,
                            };
                            return { ...f, systemIds: updated };
                          })
                        }
                        placeholder="https://ietf.org/rfc/rfc4122"
                      />
                    </FormField>
                    <FormField label={idx === 0 ? "ID" : ""}>
                      <FormInput
                        value={sid.id}
                        onChange={(v) =>
                          setSysCharsForm((f) => {
                            const updated = [...f.systemIds];
                            updated[idx] = { ...updated[idx], id: v };
                            return { ...f, systemIds: updated };
                          })
                        }
                        placeholder="550e8400-e29b-41d4-a716-446655440000"
                      />
                    </FormField>
                    <button
                      type="button"
                      onClick={() =>
                        setSysCharsForm((f) => ({
                          ...f,
                          systemIds: f.systemIds.filter(
                            (_, i) => i !== idx,
                          ),
                        }))
                      }
                      className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-500/20 text-red-500 transition-colors mb-0.5"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <FormField label="Authorization Boundary">
                <FormTextarea
                  value={sysCharsForm.authorizationBoundary}
                  onChange={(v) =>
                    setSysCharsForm((f) => ({
                      ...f,
                      authorizationBoundary: v,
                    }))
                  }
                  placeholder="Describe the authorization boundary"
                  rows={2}
                />
              </FormField>
              {/* Network Architecture - description + diagram */}
              <div className="rounded-xl border border-border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-foreground tracking-wider">
                    Network Architecture
                  </h4>
                  <div className="flex items-center gap-1.5">
                    {networkArchDiagram?.contentEvidenceId && (
                      <button
                        type="button"
                        onClick={() =>
                          setViewingDiagram({
                            title: "Network Architecture",
                            contentEvidenceId:
                              networkArchDiagram.contentEvidenceId!,
                            fileName: networkArchDiagram.fileName,
                          })
                        }
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                      >
                        <Eye className="size-3" /> View
                      </button>
                    )}
                    <label
                      className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors ${uploadingDiagram === "network" ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}
                    >
                      {uploadingDiagram === "network" ? (
                        <Loader2 className="size-3 animate-spin" />
                      ) : (
                        <Upload className="size-3" />
                      )}
                      {uploadingDiagram === "network"
                        ? "Uploading"
                        : networkArchDiagram
                          ? "Replace Diagram"
                          : "Upload Diagram"}
                      <input
                        type="file"
                        accept="image/*,.pdf,.doc,.docx"
                        className="hidden"
                        disabled={uploadingDiagram === "network"}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleDiagramUpload(file, "network");
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                </div>
                <FormTextarea
                  value={sysCharsForm.networkArchitecture}
                  onChange={(v) =>
                    setSysCharsForm((f) => ({
                      ...f,
                      networkArchitecture: v,
                    }))
                  }
                  placeholder="Describe the network architecture"
                  rows={2}
                />
                {networkArchDiagram && (
                  <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                    <FileImage className="size-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs truncate flex-1">
                      {networkArchDiagram.fileName ||
                        networkArchDiagram.file?.name ||
                        "Diagram"}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDiagramDelete("network")}
                      className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-500/20 text-muted-foreground hover:text-red-600 transition-colors"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                )}
              </div>

              {/* Data Flow - description + diagram */}
              <div className="rounded-xl border border-border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-foreground tracking-wider">
                    Data Flow
                  </h4>
                  <div className="flex items-center gap-1.5">
                    {dataFlowDiagram?.contentEvidenceId && (
                      <button
                        type="button"
                        onClick={() =>
                          setViewingDiagram({
                            title: "Data Flow",
                            contentEvidenceId:
                              dataFlowDiagram.contentEvidenceId!,
                            fileName: dataFlowDiagram.fileName,
                          })
                        }
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                      >
                        <Eye className="size-3" /> View
                      </button>
                    )}
                    <label
                      className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors ${uploadingDiagram === "dataflow" ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}
                    >
                      {uploadingDiagram === "dataflow" ? (
                        <Loader2 className="size-3 animate-spin" />
                      ) : (
                        <Upload className="size-3" />
                      )}
                      {uploadingDiagram === "dataflow"
                        ? "Uploading"
                        : dataFlowDiagram
                          ? "Replace Diagram"
                          : "Upload Diagram"}
                      <input
                        type="file"
                        accept="image/*,.pdf,.doc,.docx"
                        className="hidden"
                        disabled={uploadingDiagram === "dataflow"}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleDiagramUpload(file, "dataflow");
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                </div>
                <FormTextarea
                  value={sysCharsForm.dataFlow}
                  onChange={(v) =>
                    setSysCharsForm((f) => ({ ...f, dataFlow: v }))
                  }
                  placeholder="Describe data flows"
                  rows={2}
                />
                {dataFlowDiagram && (
                  <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                    <FileImage className="size-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs truncate flex-1">
                      {dataFlowDiagram.fileName ||
                        dataFlowDiagram.file?.name ||
                        "Diagram"}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDiagramDelete("dataflow")}
                      className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-500/20 text-muted-foreground hover:text-red-600 transition-colors"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <FormField label="Confidentiality">
                  <FormSelect
                    options={[
                      { value: "low", label: "Low" },
                      { value: "moderate", label: "Moderate" },
                      { value: "high", label: "High" },
                    ]}
                    value={sysCharsForm.confidentiality}
                    onChange={(v) =>
                      setSysCharsForm((f) => ({ ...f, confidentiality: v }))
                    }
                    placeholder="Select"
                  />
                </FormField>
                <FormField label="Integrity">
                  <FormSelect
                    options={[
                      { value: "low", label: "Low" },
                      { value: "moderate", label: "Moderate" },
                      { value: "high", label: "High" },
                    ]}
                    value={sysCharsForm.integrity}
                    onChange={(v) =>
                      setSysCharsForm((f) => ({ ...f, integrity: v }))
                    }
                    placeholder="Select"
                  />
                </FormField>
                <FormField label="Availability">
                  <FormSelect
                    options={[
                      { value: "low", label: "Low" },
                      { value: "moderate", label: "Moderate" },
                      { value: "high", label: "High" },
                    ]}
                    value={sysCharsForm.availability}
                    onChange={(v) =>
                      setSysCharsForm((f) => ({ ...f, availability: v }))
                    }
                    placeholder="Select"
                  />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">

                <FormField label="Date Authorized">
                  <FormDatePicker
                    value={sysCharsForm.dateAuthorized}
                    onChange={(v) =>
                      setSysCharsForm((f) => ({ ...f, dateAuthorized: v }))
                    }
                  />
                </FormField>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={() => setEditingSysChars(false)}
                  className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSysChars}
                  disabled={!sysCharsForm.systemName || savingSysChars}
                  className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  {savingSysChars ? "Saving" : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="divide-y divide-border">
                {sysChars.systemName && (
                  <SysCharRow icon={Server} label="System Name">
                    <span className="font-medium text-foreground">
                      {sysChars.systemName}
                    </span>
                  </SysCharRow>
                )}
                {sysChars.systemNameShort && (
                  <SysCharRow icon={Tag} label="Short Name">
                    <span className="font-medium text-foreground">
                      {sysChars.systemNameShort}
                    </span>
                  </SysCharRow>
                )}
                {sysChars.description && (
                  <SysCharRow icon={FileText} label="Description">
                    <p className="leading-relaxed">
                      {sysChars.description}
                    </p>
                  </SysCharRow>
                )}
                {sysChars.securitySensitivityLevel && (
                  <SysCharRow
                    icon={ShieldAlert}
                    label="Security Sensitivity Level"
                  >
                    <SysLevelPill
                      level={sysChars.securitySensitivityLevel}
                    />
                  </SysCharRow>
                )}
                {sysChars.systemType && (
                  <SysCharRow icon={Cpu} label="System Type">
                    <span className="capitalize">
                      {sysChars.systemType.replace(/-/g, " ")}
                    </span>
                  </SysCharRow>
                )}
                {sysChars.authorizationType && (
                  <SysCharRow icon={BadgeCheck} label="Authorization Type">
                    <span className="capitalize">
                      {sysChars.authorizationType.replace(/-/g, " ")}
                    </span>
                  </SysCharRow>
                )}
                {sysChars.deploymentModel && (
                  <SysCharRow icon={Cloud} label="Deployment Model">
                    <span className="capitalize">
                      {sysChars.deploymentModel.replace(/-/g, " ")}
                    </span>
                  </SysCharRow>
                )}
                {sysChars.serviceModel && (
                  <SysCharRow icon={Layers} label="Service Model">
                    <span className="capitalize">
                      {sysChars.serviceModel}
                    </span>
                  </SysCharRow>
                )}
                {sysChars.authorizationBoundary?.description && (
                  <SysCharRow icon={Boxes} label="Authorization Boundary">
                    <p className="leading-relaxed">
                      {sysChars.authorizationBoundary.description}
                    </p>
                  </SysCharRow>
                )}
                {sysChars.securityImpactLevel && (
                  <SysCharRow
                    icon={ShieldCheck}
                    label="Security Impact Level"
                  >
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          Confidentiality:
                        </span>
                        <SysLevelPill
                          level={
                            sysChars.securityImpactLevel[
                            "security-objective-confidentiality"
                            ]
                          }
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          Integrity:
                        </span>
                        <SysLevelPill
                          level={
                            sysChars.securityImpactLevel[
                            "security-objective-integrity"
                            ]
                          }
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          Availability:
                        </span>
                        <SysLevelPill
                          level={
                            sysChars.securityImpactLevel[
                            "security-objective-availability"
                            ]
                          }
                        />
                      </div>
                    </div>
                  </SysCharRow>
                )}
                {sysChars.status && (
                  <SysCharRow icon={Activity} label="Status">
                    <SysStatusPill status={sysChars.status} />
                  </SysCharRow>
                )}
                {sysChars.statusRemarks && (
                  <SysCharRow icon={MessageSquare} label="Status Remarks">
                    <p className="leading-relaxed">
                      {sysChars.statusRemarks}
                    </p>
                  </SysCharRow>
                )}
                {sysChars.dateAuthorized && (
                  <SysCharRow icon={Calendar} label="Date Authorized">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="size-3.5 text-muted-foreground" />
                      {toddmmyyyy(sysChars.dateAuthorized)}
                    </span>
                  </SysCharRow>
                )}
              </div>

              {/* Network Architecture - combined read-only */}
              {(sysChars.networkArchitecture?.description ||
                networkArchDiagram) && (
                  <div className="mt-4 rounded-xl border border-border bg-muted/20 p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center size-7 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                        <Network className="size-3.5" />
                      </div>
                      <h4 className="text-sm font-bold text-foreground">
                        Network Architecture
                      </h4>
                    </div>
                    {sysChars.networkArchitecture?.description && (
                      <p className="text-sm text-foreground/80 leading-relaxed">
                        {sysChars.networkArchitecture.description}
                      </p>
                    )}
                    {networkArchDiagram?.contentEvidenceId && (
                      <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
                        <FileImage className="size-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                        <span className="text-xs truncate flex-1">
                          {networkArchDiagram.fileName ||
                            networkArchDiagram.file?.name ||
                            "Diagram"}
                        </span>
                        <button
                          onClick={() =>
                            setViewingDiagram({
                              title: "Network Architecture",
                              contentEvidenceId:
                                networkArchDiagram.contentEvidenceId!,
                              fileName: networkArchDiagram.fileName,
                            })
                          }
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                        >
                          <Eye className="size-3" /> View
                        </button>
                      </div>
                    )}
                  </div>
                )}

              {/* Data Flow - combined read-only */}
              {(sysChars.dataFlow?.description || dataFlowDiagram) && (
                <div className="mt-4 rounded-xl border border-border bg-muted/20 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center size-7 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                      <Activity className="size-3.5" />
                    </div>
                    <h4 className="text-sm font-bold text-foreground">
                      Data Flow
                    </h4>
                  </div>
                  {sysChars.dataFlow?.description && (
                    <p className="text-sm text-foreground/80 leading-relaxed">
                      {sysChars.dataFlow.description}
                    </p>
                  )}
                  {dataFlowDiagram && (
                    <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
                      <FileImage className="size-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                      <span className="text-xs truncate flex-1">
                        {dataFlowDiagram.fileName ||
                          dataFlowDiagram.file?.name ||
                          "Diagram"}
                      </span>
                      <button
                        onClick={() =>
                          setViewingDiagram({
                            title: "Data Flow",
                            contentEvidenceId:
                              dataFlowDiagram.contentEvidenceId!,
                            fileName: dataFlowDiagram.fileName,
                          })
                        }
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                      >
                        <Eye className="size-3" /> View
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="text-center py-8">
            <Shield className="size-8 text-muted-foreground mb-3 mx-auto" />
            <p className="text-sm font-medium">
              No system characteristics defined
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Set up system characteristics for this SSP
            </p>
            {!isPublishedSsp && (
              <button
                onClick={() => {
                  setSysCharsForm({
                    systemName: "",
                    systemNameShort: "",
                    description: "",
                    securitySensitivityLevel: "",
                    systemType: "",
                    authorizationType: "",
                    deploymentModel: "",
                    serviceModel: "",
                    systemIds: [],
                    authorizationBoundary: "",
                    networkArchitecture: "",
                    dataFlow: "",
                    confidentiality: "",
                    integrity: "",
                    availability: "",
                    status: "",
                    statusRemarks: "",
                    dateAuthorized: "",
                  });
                  setEditingSysChars(true);
                  setSysChars({} as SystemCharacteristicsResponse);
                }}
                className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors mx-auto"
              >
                <Plus className="size-3" />
                Set Characteristics
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
